import { useEffect, useMemo, useState } from "react";
import { createCareJourneyApi } from "../api-helpers/CareJourneyApiHelper";
import { CareJourneyRoadmap } from "../api-helpers/CareJourneyTypes";

export type CareJourneyHookStatus = "loading" | "loaded" | "error";

export interface UseCareJourneyResult {
  status: CareJourneyHookStatus;
  roadmap: CareJourneyRoadmap | null;
  errorMessage: string | null;
}

// Fetches on mount, and again whenever `reloadKey` changes (the appointment
// overlay passes the route, so a form submitted elsewhere shows as done when
// the clinician comes back). The last roadmap stays in place while reloading.
// Always loads, whether or not the roadmap ends up shown (RPM Only is a display
// decision). "loaded" with a null roadmap means no journey is configured.
export function useCareJourney(
  patientId: string | undefined,
  reloadKey?: string
): UseCareJourneyResult {
  const api = useMemo(() => createCareJourneyApi(), []);
  const [status, setStatus] = useState<CareJourneyHookStatus>("loading");
  const [roadmap, setRoadmap] = useState<CareJourneyRoadmap | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!patientId) return;

    let cancelled = false;

    const fetchRoadmap = async () => {
      setStatus("loading");
      setErrorMessage(null);
      try {
        const data = await api.getRoadmap(patientId);
        if (cancelled) return;
        setRoadmap(data);
        setStatus("loaded");
      } catch {
        if (!cancelled) {
          setStatus("error");
          setErrorMessage("Unable to load patient journey.");
        }
      }
    };

    void fetchRoadmap();

    return () => {
      cancelled = true;
    };
  }, [api, patientId, reloadKey]);

  return { status, roadmap, errorMessage };
}
